import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { User, Settings, LogOut, Building2, Check } from 'lucide-react';
import { useProfile } from '@/hooks/useProfile';
import { useMyOrganizations, useSwitchActiveOrganization } from '@/hooks/useOrganizations';
import { useResetAnalyticsUser } from '@/hooks/useIdentifyUser';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuth } from '@/stores/use-auth';

export function UserMenu() {
  const { t } = useTranslation('layout');
  const navigate = useNavigate();
  const { data: profile, isLoading } = useProfile();
  const { data: organizations } = useMyOrganizations();
  const switchOrg = useSwitchActiveOrganization();
  const resetAnalytics = useResetAnalyticsUser();
  const { logout: clearAuthState } = useAuth();
  const queryClient = useQueryClient();

  const logoutMutation = useMutation({
    mutationFn: async () => {
      try {
        await api.post('auth/signout');
      } catch (error) {
        console.error('Logout API call failed:', error);
      }
    },
    onSuccess: () => {
      resetAnalytics();
      clearAuthState();
      queryClient.clear();
      const cookies = document.cookie.split(';');
      cookies.forEach((c) => {
        const cookieName = c.split('=')[0]?.trim();
        if (cookieName === 'access_token' || cookieName === 'refresh_token') {
          document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
        }
      });
      navigate('/signin');
    },
    onError: () => {
      resetAnalytics();
      clearAuthState();
      queryClient.clear();
      navigate('/signin');
    },
  });

  const handleLogout = () => {
    logoutMutation.mutate();
  };

  if (isLoading) {
    return <Skeleton className="h-8 w-8 rounded-full" />;
  }

  if (!profile) {
    return (
      <Button variant="ghost" size="sm" asChild>
        <Link to="/signin">{t('userMenu.signIn')}</Link>
      </Button>
    );
  }

  const initials = profile.display_name
    ? profile.display_name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .substring(0, 2)
    : (profile.email?.charAt(0).toUpperCase() ?? 'U');

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="relative h-8 w-8 rounded-full">
          <Avatar className="h-8 w-8">
            <AvatarImage src={profile.avatar_url ?? undefined} alt={profile.display_name ?? ''} />
            <AvatarFallback className="bg-primary/10 text-primary text-xs">
              {initials}
            </AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56" align="end" forceMount>
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium leading-none">
              {profile.display_name ?? t('userMenu.defaultName')}
            </p>
            <p className="text-xs leading-none text-muted-foreground">{profile.email}</p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {organizations && organizations.length > 1 && profile.organization ? (
          <>
            <DropdownMenuLabel className="text-xs text-muted-foreground">
              {t('userMenu.organizations')}
            </DropdownMenuLabel>
            <DropdownMenuGroup>
              {organizations.map((org) => {
                const isActive = org.organization_id === profile.organization!.organization_id;
                return (
                  <DropdownMenuItem
                    key={org.organization_id}
                    disabled={switchOrg.isPending}
                    onClick={() => {
                      if (isActive) return;
                      // Org-scoped deep links (work item, review, ADR ids) do not survive a
                      // switch, so land on the dashboard rather than refetching the old org's URL.
                      switchOrg.mutate(org.organization_id, {
                        onSuccess: () => navigate('/', { replace: true }),
                      });
                    }}
                  >
                    <Building2 className="mr-2 h-4 w-4 shrink-0 opacity-70" />
                    <span className="flex-1 truncate">{org.organization_name}</span>
                    {isActive ? <Check className="ml-2 h-4 w-4 shrink-0 text-primary" /> : null}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
          </>
        ) : null}
        <DropdownMenuGroup>
          <DropdownMenuItem onClick={() => navigate('/settings/profile')}>
            <User className="mr-2 h-4 w-4" />
            <span>{t('userMenu.profile')}</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => navigate('/settings')}>
            <Settings className="mr-2 h-4 w-4" />
            <span>{t('userMenu.settings')}</span>
          </DropdownMenuItem>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="text-destructive focus:text-destructive dark:text-red-400 dark:focus:text-red-400"
          onClick={handleLogout}
        >
          <LogOut className="mr-2 h-4 w-4" />
          <span>{t('userMenu.logOut')}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
